import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Plus, Clock, AlertTriangle, Users, CalendarDays, History, Lock, Trash2, Pencil, Crown, AlertCircle, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useMentorshipReadOnly } from '@/hooks/useMentorshipReadOnly';
import { GroupFormDialog } from '@/components/mentorships/GroupFormDialog';
import { MentorMultiSelect, type MentorOption } from '@/components/mentorships/MentorMultiSelect';
import { BilledHoursNote } from '@/components/mentorships/BilledHoursNote';
import {
  useMentorshipProject, useMentorshipGroups, useMentorshipHours, useProjectSessions,
  useProjectTasks, useMentorshipHoursLog, useMentorshipExtraWork, useAddExtraWork,
  useUpdateExtraWork, useDeleteExtraWork, useCloseMentorshipProject, useSaveMentorshipProject,
  useDeleteMentorshipGroup, useMentorshipMentorDirectory, usePeopleByIds, useMentorshipMentorReport, SESSION_STATUS_LABEL,
  billedSessionHours, billedExtraHours,
  type MentorshipGroup, type MentorshipExtraWorkRow, type MentorReportItem,
} from '@/hooks/useCustomMentorships';

const fmtHours = (n: number) => `${Math.round(n * 100) / 100} h`;
const fmtDate = (d?: string | null) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Sin fecha');
const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');


export default function MentorshipProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const isSuperAdmin = roles.includes('super_admin');
  const readOnly = useMentorshipReadOnly(projectId);

  const { data: project, isLoading } = useMentorshipProject(projectId);
  const { data: groups = [] } = useMentorshipGroups(projectId);
  const { data: hours } = useMentorshipHours(projectId, Number(project?.contracted_hours ?? 0));
  const { data: sessions = [] } = useProjectSessions(projectId);
  const { data: tasks = [] } = useProjectTasks(projectId);
  const { data: log = [] } = useMentorshipHoursLog(projectId);
  const { data: extraWork = [] } = useMentorshipExtraWork(projectId);
  const { data: mentorReport } = useMentorshipMentorReport(projectId);

  // Las tres figuras de Vivetori (mentor líder, apoyos de la mentoría y apoyo de
  // cada grupo) y el resto de mentores de la plataforma, con nombre.
  const directory = useMentorshipMentorDirectory(project);
  const nameOf = (id: string | null | undefined) => (id ? directory.nameById.get(id) ?? 'Mentor' : null);
  const leadMentorName = nameOf(directory.leadId);
  const supportMentors = directory.supportIds.map((id) => ({ id, name: nameOf(id)! }));

  // Líder del proyecto (cliente): quien esté marcado como líder en los grupos.
  const clientLeaderIds = useMemo(
    () => Array.from(new Set(groups.flatMap((g) => (g.mentorship_group_participants ?? []).filter((p) => p.is_leader).map((p) => p.user_id)))),
    [groups]
  );
  const { data: clientLeaders } = usePeopleByIds(clientLeaderIds);
  const clientLeaderNames = (clientLeaders ?? []).map((p) => p.full_name || p.email);

  const addExtraWork = useAddExtraWork();
  const updateExtraWork = useUpdateExtraWork();
  const deleteExtraWork = useDeleteExtraWork();
  const closeProject = useCloseMentorshipProject();
  const saveProject = useSaveMentorshipProject();
  const deleteGroup = useDeleteMentorshipGroup();

  const [groupDialog, setGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<MentorshipGroup | null>(null);
  const [hoursDialog, setHoursDialog] = useState(false);
  const [newHours, setNewHours] = useState('');
  const [workForm, setWorkForm] = useState({ work_date: new Date().toISOString().slice(0, 10), hours: '1', description: '', group_id: '' });
  // null = el usuario no tocó la selección: se propone el apoyo del grupo elegido (o el líder).
  const [workMentorIds, setWorkMentorIds] = useState<string[] | null>(null);
  const [editingWork, setEditingWork] = useState<MentorshipExtraWorkRow | null>(null);
  const [workEditForm, setWorkEditForm] = useState({ work_date: '', hours: '', description: '', group_id: '' });
  const [workEditMentorIds, setWorkEditMentorIds] = useState<string[]>([]);

  const extraHoursByGroup = useMemo(() => {
    const map = new Map<string, number>();
    extraWork.forEach((w) => {
      if (!w.group_id) return;
      map.set(w.group_id, (map.get(w.group_id) ?? 0) + billedExtraHours(w.hours, project?.hours_rounding));
    });
    return map;
  }, [extraWork]);



  const isFull = (hours?.usedPct ?? 0) >= 100;

  const upcoming = useMemo(
    () =>
      sessions
        .filter((s) => s.status === 'planned' || s.status === 'draft')
        .sort((a, b) => (a.scheduled_date ?? '').localeCompare(b.scheduled_date ?? ''))
        .slice(0, 10),
    [sessions]
  );
  const overdue = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled' && t.due_date && t.due_date < today);
  }, [tasks]);

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (!project) return <Card><CardContent className="py-12 text-center text-muted-foreground">Proyecto no encontrado.</CardContent></Card>;

  const assignedByOtherGroups = groups
    .filter((g) => g.id !== editingGroup?.id)
    .reduce((s, g) => s + Number(g.estimated_hours || 0), 0);

  // El apoyo de un grupo solo cuenta si sigue siendo mentor de la mentoría (apoyo
  // o el propio líder); si no hay, lo atiende el mentor líder.
  const groupSupport = (g: MentorshipGroup) =>
    g.support_mentor_id && directory.mentoriaIds.includes(g.support_mentor_id) ? g.support_mentor_id : null;
  const whoAttends = (g: MentorshipGroup | undefined) => (g && groupSupport(g)) || directory.leadId;
  const workGroup = groups.find((g) => g.id === workForm.group_id);
  const proposedWorkMentor = whoAttends(workGroup);
  const workMentorSelection = workMentorIds ?? (proposedWorkMentor ? [proposedWorkMentor] : []);

  // Opciones para atribuir horas: primero los de la mentoría (marcando el apoyo del
  // grupo elegido), luego el resto de mentores de la plataforma.
  const mentoriaOptionsFor = (g: MentorshipGroup | undefined): MentorOption[] => {
    const support = g ? groupSupport(g) : null;
    return [
      ...(directory.leadId
        ? [{ id: directory.leadId, name: leadMentorName!, role: 'lider' as const, groupSupport: directory.leadId === support }]
        : []),
      ...supportMentors.map((m) => ({ ...m, role: 'apoyo' as const, groupSupport: m.id === support })),
    ];
  };
  const otherOptions: MentorOption[] = directory.otherIds.map((id) => ({ id, name: nameOf(id)! }));
  const workEditGroup = groups.find((g) => g.id === workEditForm.group_id);
  const hasMentorOptions = !!directory.leadId || supportMentors.length + otherOptions.length > 0;
  const NO_MENTOR_ERROR = 'Marca al menos un mentor: las horas se atribuyen a quien las dio.';

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/mentorias')}>
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver
      </Button>

      {readOnly && (
        <div className="rounded-lg border bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
          Vista de solo lectura: puedes consultar el seguimiento de esta mentoría, pero no modificarla.
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <p className="text-muted-foreground text-sm">
            {project.organizations?.name} · {project.mentorship_project_types?.name ?? 'Sin tipo'} ·{' '}
            {fmtDate(project.start_date)} → {fmtDate(project.end_date)}
          </p>
          {project.scope_description && <p className="text-sm mt-2 max-w-3xl">{project.scope_description}</p>}
          {project.contract_reference && (
            <p className="text-xs text-muted-foreground mt-1">Contrato: {project.contract_reference}</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 mt-3 max-w-3xl">
            <div className="rounded-lg border p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Cliente · {project.organizations?.name ?? 'Empresa'}
              </div>
              <RoleLine
                icon={<Crown className="w-3.5 h-3.5 text-primary" />}
                label="Líder del proyecto"
                hint="Responsable por parte del cliente"
                value={clientLeaderNames.length > 0 ? clientLeaderNames.join(', ') : 'Sin marcar'}
                missing={clientLeaderNames.length === 0}
              />
            </div>
            <div className="rounded-lg border p-3 space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Vivetori</div>
              <RoleLine
                icon={<Crown className="w-3.5 h-3.5 text-primary" />}
                label="Mentor líder"
                hint="Responde por toda la mentoría"
                value={leadMentorName ?? 'Sin asignar'}
                missing={!leadMentorName}
              />
              <RoleLine
                icon={<UserCheck className="w-3.5 h-3.5 text-muted-foreground" />}
                label="Apoyo de la mentoría"
                hint="Acompaña al líder en toda la mentoría · el apoyo de cada grupo está en su tarjeta"
                value={supportMentors.length > 0 ? supportMentors.map((m) => m.name).join(', ') : 'Ninguno'}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Badge variant={project.status === 'closed' ? 'secondary' : 'default'}>
            {project.status === 'closed' ? 'Cerrado' : 'Activo'}
          </Badge>
          {isSuperAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => { setNewHours(String(project.contracted_hours)); setHoursDialog(true); }}>
                <Clock className="w-4 h-4 mr-2" /> Ampliar horas
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await closeProject.mutateAsync({ projectId: project.id, status: project.status === 'closed' ? 'active' : 'closed' });
                  toast.success(project.status === 'closed' ? 'Proyecto reabierto' : 'Proyecto cerrado');
                }}
              >
                {project.status === 'closed' ? 'Reabrir' : 'Cerrar proyecto'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tablero de horas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Bolsa de horas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Metric label="Contratadas" value={fmtHours(hours?.contracted ?? 0)} />
            <Metric label="Asignadas a grupos" value={fmtHours(hours?.assigned ?? 0)} />
            <Metric label="Planificadas" value={fmtHours(hours?.planned ?? 0)} />
            <Metric label="Ejecutadas" value={fmtHours(hours?.executed ?? 0)} />
            <Metric label="Restantes" value={fmtHours(hours?.remaining ?? 0)} />
          </div>
          <Progress value={hours?.usedPct ?? 0} />
          {isFull && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <Lock className="w-4 h-4" />
              La bolsa está agotada: no se pueden marcar nuevas sesiones ni registrar trabajo hasta ampliar las horas.
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="groups">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="groups"><Users className="w-4 h-4 mr-2" />Grupos</TabsTrigger>
          <TabsTrigger value="sessions"><CalendarDays className="w-4 h-4 mr-2" />Próximas sesiones</TabsTrigger>
          <TabsTrigger value="tasks"><AlertTriangle className="w-4 h-4 mr-2" />Tareas vencidas ({overdue.length})</TabsTrigger>
          <TabsTrigger value="extra">Trabajo fuera de sesión</TabsTrigger>
          <TabsTrigger value="mentors"><UserCheck className="w-4 h-4 mr-2" />Horas por mentor</TabsTrigger>
          <TabsTrigger value="log"><History className="w-4 h-4 mr-2" />Bitácora</TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="space-y-4 pt-4">
          <div className={readOnly ? 'hidden' : 'flex justify-end'}>
            <Button size="sm" onClick={() => { setEditingGroup(null); setGroupDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Nuevo grupo
            </Button>
          </div>
          {groups.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Aún no hay grupos en este proyecto.</CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {groups.map((g) => {
                const gSessions = sessions.filter((s) => s.group_id === g.id);
                const done = gSessions.filter((s) => s.status === 'completed');
                const sessionHours = done.reduce((s, x) => s + billedSessionHours(x.duration_minutes, project?.hours_rounding), 0);
                const support = groupSupport(g);
                const extraHours = extraHoursByGroup.get(g.id) ?? 0;
                const executed = sessionHours + extraHours;
                const gOverdue = overdue.filter((t) => t.group_id === g.id).length;

                return (
                  <Card key={g.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-base">{g.name}</CardTitle>
                          <CardDescription>{g.group_type || 'Sin tipo'} · {(g.mentorship_group_participants ?? []).length} participantes</CardDescription>
                        </div>
                        <Badge variant={g.status === 'closed' ? 'secondary' : 'outline'}>
                          {g.status === 'closed' ? 'Cerrado' : 'Activo'}
                        </Badge>
                      </div>
                      {support ? (
                        <Badge variant="secondary" className="w-fit gap-1 mt-1">
                          <UserCheck className="w-3 h-3" /> Apoyo del grupo: {nameOf(support)}
                          {support === directory.leadId ? ' (mentor líder)' : ''}
                        </Badge>
                      ) : (
                        // Sin apoyo lo atiende el líder. Si la mentoría sí tiene apoyos,
                        // probablemente falta asignarlo: se resalta más.
                        <Badge
                          variant="outline"
                          className={`w-fit gap-1 mt-1 ${
                            supportMentors.length > 0 ? 'text-amber-700 border-amber-500/60 dark:text-amber-400' : 'text-muted-foreground'
                          }`}
                        >
                          <AlertCircle className="w-3 h-3" /> Sin apoyo · lo atiende el mentor líder
                          {leadMentorName ? ` (${leadMentorName})` : ''}
                        </Badge>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="text-sm text-muted-foreground">
                        {done.length} de {gSessions.length} sesiones realizadas · {fmtHours(executed)} de {fmtHours(Number(g.estimated_hours))} estimadas
                        <div className="text-xs">
                          {fmtHours(sessionHours)} en sesiones + {fmtHours(extraHours)} fuera de sesión
                        </div>
                      </div>

                      <Progress value={g.estimated_hours > 0 ? Math.min(100, (executed / Number(g.estimated_hours)) * 100) : 0} />
                      {gOverdue > 0 && (
                        <div className="text-sm text-destructive flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" /> {gOverdue} tarea(s) vencida(s)
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => navigate(`/mentorias/${project.id}/grupo/${g.id}`)}>Abrir</Button>
                        {!readOnly && <>
                        <Button size="sm" variant="outline" onClick={() => { setEditingGroup(g); setGroupDialog(true); }}>Editar</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!confirm('¿Eliminar este grupo y todo su contenido?')) return;
                            await deleteGroup.mutateAsync({ groupId: g.id, projectId: project.id });
                            toast.success('Grupo eliminado');
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        </>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sessions" className="pt-4">
          <Card>
            <CardContent className="pt-6 space-y-2">
              {upcoming.length === 0 ? (
                <p className="text-muted-foreground text-sm">No hay sesiones próximas.</p>
              ) : (
                upcoming.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.groupName} · {fmtDate(s.scheduled_date)} {s.start_time?.slice(0, 5) ?? ''} · {s.duration_minutes} min
                        {' '}({fmtHours(billedSessionHours(s.duration_minutes, project?.hours_rounding))} de bolsa)
                      </div>
                    </div>
                    <Badge variant="outline">{SESSION_STATUS_LABEL[s.status]}</Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="pt-4">
          <Card>
            <CardContent className="pt-6 space-y-2">
              {overdue.length === 0 ? (
                <p className="text-muted-foreground text-sm">No hay tareas vencidas.</p>
              ) : (
                overdue.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <div className="font-medium text-sm">{t.title}</div>
                      <div className="text-xs text-muted-foreground">{t.groupName} · vencía el {fmtDate(t.due_date)}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/mentorias/${project.id}/grupo/${t.group_id}`)}>Ver grupo</Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="extra" className="pt-4 space-y-4">
          <Card className={readOnly ? 'hidden' : undefined}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Registrar trabajo fuera de sesión</CardTitle>
              <CardDescription>Consume horas de la misma bolsa del proyecto.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-4 items-end">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={workForm.work_date} onChange={(e) => setWorkForm({ ...workForm, work_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Horas</Label>
                <Input type="number" min={0} step="0.25" value={workForm.hours} onChange={(e) => setWorkForm({ ...workForm, hours: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Grupo (opcional)</Label>
                <Select
                  value={workForm.group_id || 'none'}
                  onValueChange={(v) => {
                    setWorkForm({ ...workForm, group_id: v === 'none' ? '' : v });
                    // Otro grupo, otra propuesta: vuelve a sugerir su apoyo (o el líder).
                    setWorkMentorIds(null);
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sin grupo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin grupo</SelectItem>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                disabled={isFull || addExtraWork.isPending}
                onClick={async () => {
                  if (!workForm.description.trim()) { toast.error('Describe el trabajo realizado'); return; }
                  if (hasMentorOptions && workMentorSelection.length === 0) { toast.error(NO_MENTOR_ERROR); return; }
                  try {
                    await addExtraWork.mutateAsync({
                      project_id: project.id,
                      group_id: workForm.group_id || null,
                      work_date: workForm.work_date,
                      hours: Number(workForm.hours || 0),
                      description: workForm.description.trim(),
                      mentorIds: workMentorSelection,
                    });
                    setWorkForm({ ...workForm, hours: '1', description: '' });
                    setWorkMentorIds(null);
                    toast.success('Trabajo registrado');
                  } catch (e: any) {
                    toast.error(e?.message ?? 'No se pudo registrar');
                  }
                }}
              >
                Registrar
              </Button>
              <BilledHoursNote rounding={project?.hours_rounding} hours={workForm.hours} className="md:col-span-4 -mt-1" />
              <div className="space-y-2 md:col-span-4">
                <Label>Descripción</Label>
                <Textarea rows={2} value={workForm.description} onChange={(e) => setWorkForm({ ...workForm, description: e.target.value })} />
              </div>
              <div className="md:col-span-4">
                <MentorMultiSelect
                  label="Mentores que hicieron el trabajo"
                  mentoriaMentors={mentoriaOptionsFor(workGroup)}
                  otherMentors={otherOptions}
                  value={workMentorSelection}
                  onChange={setWorkMentorIds}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-2">
              {extraWork.length === 0 ? (
                <p className="text-muted-foreground text-sm">Aún no hay trabajo fuera de sesión registrado.</p>
              ) : (
                extraWork.map((w) => (
                  <div key={w.id} className="flex items-start justify-between gap-3 rounded-md border p-3">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">{w.description}</div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">{w.group_name ?? 'Sin grupo'}</Badge>
                        <span>{fmtDate(w.work_date)}</span>
                        <span>
                          · {fmtHours(Number(w.hours))}
                          {billedExtraHours(w.hours, project?.hours_rounding) !== Number(w.hours) && ` (se cobran ${fmtHours(billedExtraHours(w.hours, project?.hours_rounding))})`}
                        </span>
                        {w.author_name && <span>· Registrado por {w.author_name}</span>}
                        <span>· Creado el {fmtDateTime(w.created_at)}</span>
                      </div>
                      {w.mentor_names.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1">
                          {w.mentor_names.map((n, i) => (
                            <Badge key={w.mentor_ids[i]} variant="secondary" className="text-xs">{n}</Badge>
                          ))}
                          {w.mentor_names.length > 1 && <Badge variant="outline" className="text-xs">Compartido</Badge>}
                        </div>
                      )}
                    </div>
                    <div className={readOnly ? 'hidden' : 'flex items-center gap-1'}>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingWork(w);
                          setWorkEditForm({
                            work_date: w.work_date,
                            hours: String(w.hours),
                            description: w.description ?? '',
                            group_id: w.group_id ?? '',
                          });
                          setWorkEditMentorIds(w.mentor_ids);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={async () => {
                          await deleteExtraWork.mutateAsync({
                            id: w.id,
                            projectId: project.id,
                            groupId: w.group_id,
                            hours: Number(w.hours),
                            description: w.description,
                          });
                          toast.success('Registro eliminado');
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Dialog open={!!editingWork} onOpenChange={(o) => { if (!o) setEditingWork(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar trabajo fuera de sesión</DialogTitle>
                <DialogDescription>Los cambios de horas quedan registrados en la bitácora.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Fecha</Label>
                    <Input type="date" value={workEditForm.work_date} onChange={(e) => setWorkEditForm({ ...workEditForm, work_date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Horas</Label>
                    <Input type="number" min={0} step="0.25" value={workEditForm.hours} onChange={(e) => setWorkEditForm({ ...workEditForm, hours: e.target.value })} />
                  </div>
                </div>
                <BilledHoursNote rounding={project?.hours_rounding} hours={workEditForm.hours} />
                <div className="space-y-2">
                  <Label>Grupo</Label>
                  <Select
                    value={workEditForm.group_id || 'none'}
                    onValueChange={(v) => setWorkEditForm({ ...workEditForm, group_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Sin grupo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin grupo</SelectItem>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={workEditForm.description} onChange={(e) => setWorkEditForm({ ...workEditForm, description: e.target.value })} />
                </div>
                <MentorMultiSelect
                  label="Mentores que hicieron el trabajo"
                  mentoriaMentors={mentoriaOptionsFor(workEditGroup)}
                  otherMentors={otherOptions}
                  value={workEditMentorIds}
                  onChange={setWorkEditMentorIds}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingWork(null)}>Cancelar</Button>
                <Button
                  disabled={updateExtraWork.isPending}
                  onClick={async () => {
                    if (!editingWork) return;
                    if (!workEditForm.description.trim()) { toast.error('Describe el trabajo realizado'); return; }
                    if (hasMentorOptions && workEditMentorIds.length === 0) { toast.error(NO_MENTOR_ERROR); return; }
                    try {
                      await updateExtraWork.mutateAsync({
                        id: editingWork.id,
                        project_id: project.id,
                        group_id: workEditForm.group_id || null,
                        work_date: workEditForm.work_date,
                        hours: Number(workEditForm.hours || 0),
                        description: workEditForm.description.trim(),
                        previous_hours: Number(editingWork.hours),
                        mentorIds: workEditMentorIds,
                      });
                      toast.success('Registro actualizado');
                      setEditingWork(null);
                    } catch (e: any) {
                      toast.error(e?.message ?? 'No se pudo actualizar');
                    }
                  }}
                >
                  Guardar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </TabsContent>

        <TabsContent value="log" className="pt-4">
          <Card>
            <CardContent className="pt-6 space-y-2">
              {log.length === 0 ? (
                <p className="text-muted-foreground text-sm">Sin movimientos registrados.</p>
              ) : (
                log.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <div className="text-sm">{entry.description}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(entry.created_at).toLocaleString('es-CO')}
                      </div>
                      {entry.mentors.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          {entry.mentors.map((m) => (
                            <Badge key={m.mentor_id} variant="secondary" className="text-xs gap-1">
                              {m.is_primary && <Crown className="w-3 h-3" />}
                              {m.full_name ?? 'Mentor'}
                            </Badge>
                          ))}
                          {entry.mentors.length > 1 && (
                            <Badge variant="outline" className="text-xs">Compartido</Badge>
                          )}
                        </div>
                      )}
                    </div>
                    {entry.hours_delta !== null && (
                      <Badge variant="outline">{Number(entry.hours_delta) > 0 ? '+' : ''}{fmtHours(Number(entry.hours_delta))}</Badge>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mentors" className="pt-4 space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-1 text-sm">
              <div>
                Consolidado de la mentoría: <span className="font-semibold">{fmtHours(mentorReport?.consolidatedHours ?? 0)}</span>
                {' '}(cada registro contado una sola vez; igual a las horas ejecutadas de la bolsa).
              </div>
              <div className="text-xs text-muted-foreground">
                Un registro con varios mentores aparece completo en el reporte de cada uno, marcado como compartido:
                no sumes los totales por mentor para consolidar.
              </div>
            </CardContent>
          </Card>
          {(mentorReport?.rows ?? []).length === 0 && (mentorReport?.unattributed ?? []).length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Aún no hay horas ejecutadas.</CardContent></Card>
          ) : (
            <>
              {(mentorReport?.rows ?? []).map((row) => (
                <Card key={row.mentorId}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex flex-wrap items-center gap-2">
                      {row.fullName}
                      <Badge variant="outline">{fmtHours(row.hours)}</Badge>
                      {row.sharedHours > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                          de las cuales {fmtHours(row.sharedHours)} compartidas
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {row.items.map((item) => <MentorReportLine key={`${item.kind}-${item.id}`} item={item} />)}
                  </CardContent>
                </Card>
              ))}
              {(mentorReport?.unattributed ?? []).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-destructive">
                      <AlertCircle className="w-4 h-4" /> Sin mentor atribuido
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(mentorReport?.unattributed ?? []).map((item) => (
                      <MentorReportLine key={`${item.kind}-${item.id}`} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      <GroupFormDialog
        open={groupDialog}
        onOpenChange={setGroupDialog}
        projectId={project.id}
        organizationId={project.organization_id}
        contractedHours={Number(project.contracted_hours)}
        assignedByOtherGroups={assignedByOtherGroups}
        group={editingGroup}
        leadMentor={directory.leadId ? { id: directory.leadId, name: leadMentorName! } : null}
        supportMentors={supportMentors}
        vivetoriIds={[...directory.mentoriaIds, ...directory.otherIds]}
      />

      <Dialog open={hoursDialog} onOpenChange={setHoursDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Horas contratadas</DialogTitle>
            <DialogDescription>Ampliar la bolsa queda registrado en la bitácora.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Horas</Label>
            <Input type="number" min={0} step="0.5" value={newHours} onChange={(e) => setNewHours(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursDialog(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                await saveProject.mutateAsync({
                  id: project.id,
                  organization_id: project.organization_id,
                  name: project.name,
                  scope_description: project.scope_description,
                  type_id: project.type_id,
                  contracted_hours: Number(newHours || 0),
                  start_date: project.start_date,
                  end_date: project.end_date,
                  lead_mentor_id: project.lead_mentor_id,
                  contract_reference: project.contract_reference,
                  status: project.status,
                } as any);
                setHoursDialog(false);
                toast.success('Horas actualizadas');
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MentorReportLine({ item }: { item: MentorReportItem }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border p-2 text-sm">
      <div className="min-w-0">
        <div className="truncate">{item.label}</div>
        <div className="text-xs text-muted-foreground">
          {item.kind === 'session' ? 'Sesión' : 'Trabajo fuera de sesión'}
          {item.groupName ? ` · ${item.groupName}` : ''} · {fmtDate(item.date)}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {item.shared && <Badge variant="outline" className="text-xs">Compartido</Badge>}
        <Badge variant="secondary">{fmtHours(item.hours)}</Badge>
      </div>
    </div>
  );
}

function RoleLine({ icon, label, hint, value, missing }: { icon: ReactNode; label: string; hint: string; value: string; missing?: boolean }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0">
        <div>
          <span className="text-muted-foreground">{label}:</span>{' '}
          <span className={`font-medium ${missing ? 'text-amber-700 dark:text-amber-400' : ''}`}>{value}</span>
        </div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
